import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Sparkles, FileDown, AlertTriangle, Activity, LogOut, LogIn, BedDouble,
  Plus, Trash2, Save, Database, Settings2, Building2,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { api, apiError } from "../../lib/api";
import { AiCompare } from "./AiCompare";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { DateField } from "./DateField";

const PERIOD_OPTS = [
  ["today", "Aujourd'hui"], ["week", "Cette semaine"], ["month", "Ce mois"],
  ["year", "Cette année"], ["custom", "Personnalisé"],
];
const DETAIL_OPTS = [["global", "Toute la période"], ["secteur", "Par secteur"], ["service", "Par service"]];

function Markdown({ text }) {
  const lines = (text || "").split("\n");
  const out = []; let bullets = [];
  const flush = (i) => { if (bullets.length) { out.push(<ul key={`u${i}`} className="my-2 ml-4 list-disc space-y-1">{bullets}</ul>); bullets = []; } };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-navy">{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>);
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (!t || t === "---") { flush(i); return; }
    if (t.startsWith("## ")) { flush(i); out.push(<h4 key={i} className="mt-3 mb-1 font-display text-sm font-semibold text-navy">{t.slice(3)}</h4>); }
    else if (t.startsWith("- ") || t.startsWith("* ")) bullets.push(<li key={i} className="text-sm text-slate-600">{inline(t.slice(2))}</li>);
    else { flush(i); out.push(<p key={i} className="my-1 text-sm text-slate-600">{inline(t)}</p>); }
  });
  flush("end");
  return <div data-testid="mgmt-ai-content">{out}</div>;
}

const KpiCard = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div className={`rounded-2xl border p-5 ${tone}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80"><Icon size={15} /> {label}</div>
    <p className="mt-2 font-display text-3xl font-bold">{value}</p>
    {sub && <p className="mt-1 text-[11px] opacity-70">{sub}</p>}
  </div>
);

export function ManagementManager() {
  const [opts, setOpts] = useState({ secteurs: [] });
  const [period, setPeriod] = useState("month");
  const [detail, setDetail] = useState("global");
  const [secteur, setSecteur] = useState("Hôpital (global)");
  const [service, setService] = useState("Tous les services");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [dash, setDash] = useState(null);
  const [ts, setTs] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);

  const services = (opts.secteurs.find((s) => s.secteur === secteur)?.services) || ["Tous les services"];

  const qp = useCallback(() => {
    const p = new URLSearchParams({ period, detail, secteur, service });
    if (period === "custom") { if (frm) p.set("frm", frm); if (to) p.set("to", to); }
    return p.toString();
  }, [period, detail, secteur, service, frm, to]);

  const load = useCallback(async () => {
    if (period === "custom" && (!frm || !to)) return;
    try {
      const [d, t] = await Promise.all([
        api.get(`/admin/management/dashboard?${qp()}`),
        api.get(`/admin/management/timeseries?${qp()}`),
      ]);
      setDash(d.data); setTs(t.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [qp, period, frm, to]);

  useEffect(() => { api.get("/admin/management/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    setAiBusy(true); setAi(null);
    try {
      const { data } = await api.post(`/admin/management/analyse-ia?${qp()}`, {}, { timeout: 90000 });
      setAi(data);
      if (!data.ai_available) toast.info("Analyse statistique affichée (clé IA indisponible)");
    } catch (e) { toast.error(apiError(e, "Analyse impossible")); } finally { setAiBusy(false); }
  };

  const doExport = async (format) => {
    try {
      const res = await api.get(`/admin/management/export?${qp()}&format=${format}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `management-${period}.${format}`;
      a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast.success("Export généré");
    } catch { toast.error("Export impossible"); }
  };

  const seed = async () => {
    setSeedBusy(true);
    try { const { data } = await api.post("/admin/management/seed-demo"); toast.success(`Données générées : ${data.events} évén., ${data.beds} secteurs de lits, ${data.stock} articles`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSeedBusy(false); }
  };

  const alerts = dash?.stock_alerts || [];

  return (
    <div data-testid="management-dashboard">
      {/* En-tête CARIS Santé */}
      <div className="mb-5 flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[#0A2540] to-[#0E7C7B] px-6 py-4 text-white">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15"><Activity size={22} /></div>
        <div>
          <h2 className="font-display text-lg font-bold leading-tight">Tableau de bord de management stratégique</h2>
          <p className="text-xs text-white/70">Cabinet CARIS Santé — Pilotage hospitalier</p>
        </div>
        <span className="ml-auto rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">CAB</span>
      </div>

      {/* Bandeau alerte stock */}
      {alerts.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="mgmt-stock-alert">
          <AlertTriangle size={17} className="shrink-0" />
          <span className="font-semibold">Alerte stock :</span>
          <span>{alerts.length} article(s) sous le seuil —</span>
          <span className="text-red-600">{alerts.map((a) => `${a.nom} (${a.quantite}/${a.seuil_alerte})`).join(" · ")}</span>
        </div>
      )}

      <Tabs defaultValue="dashboard">
        <TabsList className="mb-5 bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="mgmt-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="saisie" data-testid="mgmt-tab-saisie">Saisie</TabsTrigger>
          <TabsTrigger value="config" data-testid="mgmt-tab-config">Configuration des listes</TabsTrigger>
        </TabsList>

        {/* ===== DASHBOARD ===== */}
        <TabsContent value="dashboard">
          {/* Filtres */}
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Période</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="mt-1 h-10" data-testid="mgmt-period"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIOD_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Détail</Label>
                <Select value={detail} onValueChange={setDetail}>
                  <SelectTrigger className="mt-1 h-10" data-testid="mgmt-detail"><SelectValue /></SelectTrigger>
                  <SelectContent>{DETAIL_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Secteur</Label>
                <Select value={secteur} onValueChange={(v) => { setSecteur(v); setService("Tous les services"); }}>
                  <SelectTrigger className="mt-1 h-10" data-testid="mgmt-secteur"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts.secteurs.map((s) => <SelectItem key={s.secteur} value={s.secteur}>{s.secteur}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-500">Service de soins</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger className="mt-1 h-10" data-testid="mgmt-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tous les services">Tous les services</SelectItem>
                    {services.filter((s) => s !== "Tous les services").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {period === "custom" && (
              <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="mgmt-date-range">
                <div><Label className="text-[11px] text-slate-500">Du</Label><DateField value={frm} onChange={setFrm} /></div>
                <div><Label className="text-[11px] text-slate-500">Au</Label><DateField value={to} onChange={setTo} /></div>
              </div>
            )}
            {dash && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#0E7C7B]/10 px-3 py-1 text-xs font-semibold text-[#0E7C7B]" data-testid="mgmt-scope">
                <Building2 size={13} /> {dash.scope_label} · {dash.period_label}
              </div>
            )}
          </div>

          {!dash ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>
          ) : (
            <>
              {/* GESTION */}
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Gestion</p>
              <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard testid="mgmt-kpi-admissions" icon={LogIn} label="Admissions" value={dash.kpis.admissions} tone="border-emerald-200 bg-emerald-50 text-emerald-800" />
                <KpiCard testid="mgmt-kpi-sorties" icon={LogOut} label="Sorties" value={dash.kpis.sorties} tone="border-blue-200 bg-blue-50 text-blue-800" />
                <KpiCard testid="mgmt-kpi-critiques" icon={Activity} label="Patients critiques" value={dash.kpis.patients_critiques} tone="border-red-200 bg-red-50 text-red-800" />
                <KpiCard testid="mgmt-kpi-occupation" icon={BedDouble} label="Taux d'occupation des lits" value={`${dash.kpis.taux_occupation} %`} sub={`${dash.kpis.lits_occupes} / ${dash.kpis.lits_total} lits`} tone="border-amber-200 bg-amber-50 text-amber-800" />
              </div>

              {/* Répartition admissions */}
              <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="mgmt-repartition">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-sm font-semibold text-navy">Admissions par {dash.repartition.group_by === "service" ? "service" : "secteur"}</h3>
                  <span className="text-xs text-slate-500">Total : {dash.repartition.total}</span>
                </div>
                <div className="mt-3 space-y-3">
                  {dash.repartition.items.length === 0 && <p className="text-xs text-slate-400">Aucune admission sur la période.</p>}
                  {dash.repartition.items.map((it) => (
                    <div key={it.name} data-testid={`mgmt-rep-${it.name}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{it.name}</span>
                        <span><b className="font-display text-lg text-navy">{it.count}</b> <span className="text-xs text-slate-400">{it.pct}%</span></span>
                      </div>
                      <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#0E7C7B]" style={{ width: `${it.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* STATISTIQUES */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mgmt-statistiques">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Statistiques</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" onClick={runAi} disabled={aiBusy} data-testid="mgmt-analyse-ia" className="rounded-full bg-violet-600 px-3 text-xs text-white hover:bg-violet-700">
                      {aiBusy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Analyse…</> : <><Sparkles size={13} className="mr-1" /> Analyse IA</>}
                    </Button>
                    <AiCompare moduleKey="management" params={{ period, frm, to, secteur, service }} />
                    <Button size="sm" variant="outline" onClick={() => doExport("pdf")} data-testid="mgmt-export-pdf" className="rounded-full text-xs"><FileDown size={13} className="mr-1" /> PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => doExport("docx")} data-testid="mgmt-export-docx" className="rounded-full text-xs"><FileDown size={13} className="mr-1" /> Word</Button>
                    <Button size="sm" variant="outline" onClick={() => doExport("xlsx")} data-testid="mgmt-export-xlsx" className="rounded-full text-xs"><FileDown size={13} className="mr-1" /> Excel</Button>
                  </div>
                </div>
                <div className="mt-4 h-72">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <LineChart data={ts?.points || []} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="admissions" name="Admissions" stroke="#0E7C7B" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="sorties" name="Sorties" stroke="#2563EB" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="critiques" name="Patients critiques" stroke="#DC2626" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Indicateurs statistiques Y, R², p */}
                {ts?.stats && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {Object.entries(ts.stats).map(([k, s]) => (
                      <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`mgmt-stat-${k}`}>
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-navy"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />{s.label}</span>
                        {s.regression ? (
                          <>
                            <p className="mt-1.5 font-mono text-xs text-slate-700">{s.regression.equation}</p>
                            <p className="mt-1 text-[11px] text-slate-500">R² = <b>{s.regression.r2}</b> · p = <b>{s.regression.p_value}</b> · <span className={s.regression.significant ? "text-emerald-600" : "text-slate-400"}>{s.regression.significant ? "significatif" : "non signif."}</span></p>
                            <p className="text-[11px] text-slate-500">{s.regression.direction === "hausse" ? "↗ hausse" : s.regression.direction === "baisse" ? "↘ baisse" : "→ stable"} · total {s.total} · moy. {s.moyenne}</p>
                          </>
                        ) : <p className="mt-1.5 text-[11px] italic text-slate-400">Données insuffisantes pour la régression.</p>}
                      </div>
                    ))}
                  </div>
                )}

                {ai?.narrative && (
                  <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/40 p-4" data-testid="mgmt-ai-narrative">
                    <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-navy"><Sparkles size={14} className="text-violet-600" /> Analyse IA</p>
                    <Markdown text={ai.narrative} />
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* ===== SAISIE ===== */}
        <TabsContent value="saisie"><SaisiePanel opts={opts} onChange={load} seed={seed} seedBusy={seedBusy} /></TabsContent>

        {/* ===== CONFIG ===== */}
        <TabsContent value="config"><ConfigListes opts={opts} setOpts={setOpts} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Saisie : événements, lits, stock ---------- */
function SaisiePanel({ opts, onChange, seed, seedBusy }) {
  const [events, setEvents] = useState([]);
  const [beds, setBeds] = useState([]);
  const [stock, setStock] = useState([]);
  const [ev, setEv] = useState({ kind: "admission", secteur: "", service: "", date: "", critique: false });
  const [art, setArt] = useState({ nom: "", secteur: "", quantite: 0, unite: "u", seuil_alerte: 0 });

  const reload = useCallback(async () => {
    const [e, b, s] = await Promise.all([
      api.get("/admin/management/events"), api.get("/admin/management/beds"), api.get("/admin/management/stock"),
    ]);
    setEvents(e.data.events); setBeds(b.data.beds.length ? b.data.beds : opts.secteurs.filter((x) => x.secteur !== "Hôpital (global)").map((x) => ({ secteur: x.secteur, lits_total: 0, lits_occupes: 0 }))); setStock(s.data.stock);
  }, [opts]);
  useEffect(() => { reload(); }, [reload]);

  const addEvent = async () => {
    if (!ev.secteur) { toast.error("Sélectionnez un secteur"); return; }
    try { await api.post("/admin/management/events", ev); toast.success("Événement enregistré"); setEv({ ...ev, service: "", date: "", critique: false }); reload(); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delEvent = async (id) => { await api.delete(`/admin/management/events/${id}`); reload(); onChange(); };
  const saveBeds = async () => { try { await api.put("/admin/management/beds", { beds }); toast.success("Lits enregistrés"); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const addArt = async () => {
    if (!art.nom) { toast.error("Nom de l'article requis"); return; }
    try { await api.post("/admin/management/stock", art); toast.success("Article ajouté"); setArt({ nom: "", secteur: "", quantite: 0, unite: "u", seuil_alerte: 0 }); reload(); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delArt = async (id) => { await api.delete(`/admin/management/stock/${id}`); reload(); onChange(); };
  const secteurs = opts.secteurs.filter((s) => s.secteur !== "Hôpital (global)");
  const evServices = (opts.secteurs.find((s) => s.secteur === ev.secteur)?.services) || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={seed} disabled={seedBusy} data-testid="mgmt-seed" className="rounded-full text-xs">
          {seedBusy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Database size={13} className="mr-1" />} Générer des données de démonstration
        </Button>
      </div>

      {/* Événements */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-display text-sm font-semibold text-navy">Admissions / Sorties</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
          <Select value={ev.kind} onValueChange={(v) => setEv({ ...ev, kind: v })}><SelectTrigger data-testid="mgmt-ev-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admission">Admission</SelectItem><SelectItem value="sortie">Sortie</SelectItem></SelectContent></Select>
          <Select value={ev.secteur} onValueChange={(v) => setEv({ ...ev, secteur: v, service: "" })}><SelectTrigger data-testid="mgmt-ev-secteur"><SelectValue placeholder="Secteur" /></SelectTrigger><SelectContent>{secteurs.map((s) => <SelectItem key={s.secteur} value={s.secteur}>{s.secteur}</SelectItem>)}</SelectContent></Select>
          <Select value={ev.service} onValueChange={(v) => setEv({ ...ev, service: v })}><SelectTrigger data-testid="mgmt-ev-service"><SelectValue placeholder="Service" /></SelectTrigger><SelectContent>{evServices.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <DateField value={ev.date} onChange={(v) => setEv({ ...ev, date: v })} />
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={ev.critique} onChange={(e) => setEv({ ...ev, critique: e.target.checked })} data-testid="mgmt-ev-critique" className="accent-red-500" /> Critique</label>
          <Button onClick={addEvent} data-testid="mgmt-ev-add" className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]"><Plus size={15} className="mr-1" /> Ajouter</Button>
        </div>
        <div className="mt-3 max-h-56 overflow-y-auto">
          {events.slice(0, 40).map((e) => (
            <div key={e.id} className="flex items-center justify-between border-b border-slate-50 py-1.5 text-xs">
              <span><span className={`mr-2 rounded-full px-2 py-0.5 font-semibold ${e.kind === "admission" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{e.kind}</span>{e.secteur}{e.service ? ` · ${e.service}` : ""}{e.critique ? " · 🔴" : ""} <span className="text-slate-400">{new Date(e.date).toLocaleDateString("fr-FR")}</span></span>
              <button onClick={() => delEvent(e.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Lits */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold text-navy"><BedDouble size={16} /> Lits par secteur (occupation)</h3>
        <div className="space-y-2">
          {beds.map((b, i) => (
            <div key={b.secteur} className="grid grid-cols-3 items-center gap-2 text-sm">
              <span className="text-slate-700">{b.secteur}</span>
              <Input type="number" value={b.lits_total} data-testid={`mgmt-bed-total-${i}`} onChange={(e) => setBeds(beds.map((x, j) => j === i ? { ...x, lits_total: +e.target.value } : x))} placeholder="Lits total" className="h-9" />
              <Input type="number" value={b.lits_occupes} data-testid={`mgmt-bed-occ-${i}`} onChange={(e) => setBeds(beds.map((x, j) => j === i ? { ...x, lits_occupes: +e.target.value } : x))} placeholder="Occupés" className="h-9" />
            </div>
          ))}
        </div>
        <Button onClick={saveBeds} data-testid="mgmt-bed-save" size="sm" className="mt-3 rounded-full bg-navy text-white"><Save size={14} className="mr-1" /> Enregistrer les lits</Button>
      </div>

      {/* Stock */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-display text-sm font-semibold text-navy">Stock / articles</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
          <Input value={art.nom} onChange={(e) => setArt({ ...art, nom: e.target.value })} placeholder="Article" data-testid="mgmt-art-nom" className="h-9 lg:col-span-2" />
          <Select value={art.secteur || "none"} onValueChange={(v) => setArt({ ...art, secteur: v === "none" ? "" : v })}><SelectTrigger data-testid="mgmt-art-secteur"><SelectValue placeholder="Secteur" /></SelectTrigger><SelectContent><SelectItem value="none">— Tous —</SelectItem>{secteurs.map((s) => <SelectItem key={s.secteur} value={s.secteur}>{s.secteur}</SelectItem>)}</SelectContent></Select>
          <Input type="number" value={art.quantite} onChange={(e) => setArt({ ...art, quantite: +e.target.value })} placeholder="Qté" data-testid="mgmt-art-qte" className="h-9" />
          <Input type="number" value={art.seuil_alerte} onChange={(e) => setArt({ ...art, seuil_alerte: +e.target.value })} placeholder="Seuil" data-testid="mgmt-art-seuil" className="h-9" />
          <Button onClick={addArt} data-testid="mgmt-art-add" className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]"><Plus size={15} className="mr-1" /> Ajouter</Button>
        </div>
        <div className="mt-3 max-h-56 overflow-y-auto">
          {stock.map((s) => (
            <div key={s.id} className={`flex items-center justify-between border-b border-slate-50 py-1.5 text-xs ${s.quantite <= s.seuil_alerte ? "text-red-600" : ""}`}>
              <span>{s.quantite <= s.seuil_alerte && "⚠️ "}{s.nom} — {s.quantite} {s.unite} (seuil {s.seuil_alerte}){s.secteur ? ` · ${s.secteur}` : ""}</span>
              <button onClick={() => delArt(s.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Configuration des listes ---------- */
function ConfigListes({ opts, setOpts }) {
  const [secteurs, setSecteurs] = useState([]);
  useEffect(() => { setSecteurs(JSON.parse(JSON.stringify(opts.secteurs || []))); }, [opts.secteurs]);

  const save = async () => {
    try { const { data } = await api.put("/admin/management/lists", { secteurs }); setOpts((o) => ({ ...o, secteurs: data.secteurs })); toast.success("Listes enregistrées"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="mgmt-config">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-sm font-semibold text-navy"><Settings2 size={16} /> Secteurs de soins & services</h3>
        <Button size="sm" onClick={() => setSecteurs([...secteurs, { secteur: "Nouveau secteur", services: [] }])} data-testid="mgmt-config-add-secteur" variant="outline" className="rounded-full text-xs"><Plus size={13} className="mr-1" /> Secteur</Button>
      </div>
      <div className="space-y-4">
        {secteurs.map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`mgmt-config-secteur-${i}`}>
            <div className="flex items-center gap-2">
              <Input value={s.secteur} onChange={(e) => setSecteurs(secteurs.map((x, j) => j === i ? { ...x, secteur: e.target.value } : x))} className="h-9 font-semibold" data-testid={`mgmt-config-secteur-name-${i}`} />
              <button onClick={() => setSecteurs(secteurs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {s.services.map((svc, k) => (
                <span key={k} className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs text-slate-600 shadow-sm">
                  {svc}
                  <button onClick={() => setSecteurs(secteurs.map((x, j) => j === i ? { ...x, services: x.services.filter((_, m) => m !== k) } : x))} className="text-slate-300 hover:text-red-500">×</button>
                </span>
              ))}
              <input placeholder="+ service (Entrée)" data-testid={`mgmt-config-add-service-${i}`}
                onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setSecteurs(secteurs.map((x, j) => j === i ? { ...x, services: [...x.services, e.target.value.trim()] } : x)); e.target.value = ""; } }}
                className="w-40 rounded-full border border-dashed border-slate-300 bg-transparent px-2.5 py-1 text-xs outline-none focus:border-[#0E7C7B]" />
            </div>
          </div>
        ))}
      </div>
      <Button onClick={save} data-testid="mgmt-config-save" className="mt-4 rounded-full bg-navy text-white"><Save size={14} className="mr-1" /> Enregistrer les listes</Button>
    </div>
  );
}
